import { Icon, IconButton, Tooltip } from '@elipssolution/harfang';
import { mdiChevronLeftCircle, mdiChevronRightCircle } from '@mdi/js';
import { Paper, Typography, styled } from '@mui/material';
import clsx from 'clsx';
import { format, isSameMonth, isSameYear, subYears } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useMemo } from 'react';

import { N_COLOR, N_MINUS_1_COLOR } from './utils/chart';
import useFormat from '../../../../hooks/useFormat';
import { PeriodicityEnum } from '../../../../src/types/revenue';
import { formatCurrency } from '../../../../utils/numberFormatter';

const TODAY_DATE = new Date();

const PeriodBannerWrapper = styled('div')(({ theme: { spacing, transitions } }) => ({
	display: 'flex',

	'&.isMobileFormat.hasNavigationButtons': {
		marginLeft: spacing(-1.5),
		marginRight: spacing(-1.5),
	},

	'&:not(.isMobileFormat)': {
		position: 'absolute',
		top: spacing(-1),
		right: spacing(-1),
		alignItems: 'center',

		'& > button, & > div.navigationIconButton': {
			opacity: 0,
			transition: transitions.create('opacity'),
		},

		'&:hover > button, &:hover > div.navigationIconButton': {
			opacity: 1,

			'&.disabled': {
				pointerEvents: 'none',
			},
		},
	},
}));

const PeriodIndicatorContainer = styled(Paper)(({ theme: { palette, spacing, shape } }) => ({
	display: 'flex',
	flexDirection: 'column',

	'&:first-of-type': {
		backgroundColor: N_MINUS_1_COLOR,
		color: palette.getContrastText(N_MINUS_1_COLOR),
		marginRight: spacing(1),
	},

	'&:nth-of-type(2)': {
		backgroundColor: N_COLOR,
		color: palette.getContrastText(N_COLOR),
	},

	'&.isMobileFormat': {
		padding: spacing(0.5, 1),
		borderRadius: shape.borderRadius,
		width: `calc(50% - ${spacing(2.5)})`,
		maxWidth: 'unset',

		'&.hasNavigationButtons': {
			width: `calc(50% - ${spacing(2.5)} - 48px)`,
		},
	},

	'&:not(.isMobileFormat)': {
		position: 'relative',
		width: 125,
		alignItems: 'center',
		textAlign: 'center',
		justifyContent: 'center',
		padding: spacing(0.5, 1),
		userSelect: 'none',
	},
}));

const formatIndicatorLabel = ({ date, periodicity }: { date: Date; periodicity: PeriodicityEnum }) => {
	if (periodicity === PeriodicityEnum.ANNUALLY) {
		return format(date, 'yyyy');
	}

	const formattedDate = format(date, 'MMM yyyy', { locale: fr });

	return `${formattedDate.charAt(0).toUpperCase()}${formattedDate.slice(1)}`;
};

type PeriodIndicatorProps = {
	balance: string;
	label: string;
	hasNavigationButtons: boolean;
};

const PeriodIndicator = ({ label, balance, hasNavigationButtons }: PeriodIndicatorProps) => {
	const { isMobileFormat } = useFormat();

	const formattedNumber = formatCurrency(balance);

	return (
		<Tooltip content={formattedNumber}>
			<PeriodIndicatorContainer className={clsx({ isMobileFormat, hasNavigationButtons })}>
				<Typography variant={!isMobileFormat ? 'inherit' : 'caption'} fontWeight={!isMobileFormat ? 'bold' : 'inherit'}>
					{label}
				</Typography>
				<Typography maxWidth="100%" noWrap>
					{formattedNumber}
				</Typography>
			</PeriodIndicatorContainer>
		</Tooltip>
	);
};

type PeriodBannerProps = {
	disabled?: boolean;
	balance: string;
	date: Date;
	periodicity: PeriodicityEnum;
	previousBalance: string;
	onDateChange?: (shift: number) => void;
};

const PeriodBanner = ({
	disabled = false,
	balance,
	date,
	periodicity,
	previousBalance,
	onDateChange,
}: PeriodBannerProps) => {
	const { isMobileFormat } = useFormat();

	const previousIndicatorLabel = useMemo(
		() => formatIndicatorLabel({ periodicity, date: subYears(date, 1) }),
		[date, periodicity],
	);

	const focusedIndicatorLabel = formatIndicatorLabel({ date, periodicity });

	const isNextButtonDisabled =
		periodicity === PeriodicityEnum.ANNUALLY ? isSameYear(date, TODAY_DATE) : isSameMonth(date, TODAY_DATE);

	const handleMinusClick = () => onDateChange?.(-1);
	const handlePlusClick = () => onDateChange?.(1);

	return (
		<PeriodBannerWrapper className={clsx({ isMobileFormat, hasNavigationButtons: !!onDateChange })}>
			{onDateChange && (
				<IconButton
					className={clsx('navigationIconButton', { disabled })}
					disabled={disabled}
					onClick={handleMinusClick}
				>
					<Icon path={mdiChevronLeftCircle} />
				</IconButton>
			)}

			<PeriodIndicator balance={previousBalance} label={previousIndicatorLabel} hasNavigationButtons={!!onDateChange} />
			<PeriodIndicator balance={balance} label={focusedIndicatorLabel} hasNavigationButtons={!!onDateChange} />

			{onDateChange && (
				<IconButton
					className={clsx('navigationIconButton', { disabled: isNextButtonDisabled || disabled })}
					disabled={isNextButtonDisabled || disabled}
					onClick={handlePlusClick}
				>
					<Icon path={mdiChevronRightCircle} />
				</IconButton>
			)}
		</PeriodBannerWrapper>
	);
};

export default PeriodBanner;
